import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowsClockwise, Eye, Prohibit } from '@phosphor-icons/react'
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from '@alphabait/ui'
import {
  useCampaignClient,
  useIntersolveClassicClient,
  useUserClient,
} from '@/lib/api'
import Loading from '@/components/loading'

interface CustomerOption {
  id: string
  name: string
}

// Mirrors CancellableAttemptSchema in intersolve-classic.contract.ts.
interface CancellableRow {
  attemptId: string
  status: string
  refPos: string
  cardIdLast4: string | null
  intersolveTransactionId: string | null
  value: string
  currency: string
  ean: string
  customerId: string
  integrationName: string
  incentiveName: string | null
  campaignName: string | null
  isTest: boolean | null
  createdAt: string | Date
}

interface CancelOutcome {
  attemptId: string
  refPos: string | null
  outcome: 'cancelled' | 'would_cancel' | 'failed' | 'skipped'
  via: 'transactionId' | 'refPos' | null
  reason: string | null
}

interface CancelResult {
  dryRun: boolean
  requested: number
  cancelled: number
  wouldCancel: number
  failed: number
  skipped: number
  results: CancelOutcome[]
}

// Values are stored in minor units (cents); display in the row's currency.
const formatValue = (value: string, currency: string) => {
  const minor = Number(value)
  if (Number.isNaN(minor)) return `${value} ${currency}`
  try {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency,
    }).format(minor / 100)
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`
  }
}

const formatDate = (value: string | Date) =>
  new Date(value).toLocaleString('nl-NL')

export default function CancelClassicPage() {
  const classicClient = useIntersolveClassicClient()
  const campaignClient = useCampaignClient()
  const userClient = useUserClient()
  const { toast } = useToast()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [customerId, setCustomerId] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [result, setResult] = useState<CancelResult | null>(null)

  const { data: userResponse, isLoading: userLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => await userClient.getMe.query(),
  })
  const isSuperadmin =
    userResponse?.status === 200 &&
    (userResponse.body as { role?: string })?.role === 'Superadmin'

  const { data: customersResponse } = useQuery({
    queryKey: ['cancelClassicCustomers'],
    queryFn: async () => await campaignClient.customers.query(),
    enabled: isSuperadmin,
  })
  const customers: CustomerOption[] =
    customersResponse?.status === 200
      ? (customersResponse.body as CustomerOption[])
      : []

  const {
    data: listResponse,
    isLoading: listLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['intersolveClassicCancellable', customerId],
    queryFn: async () =>
      await classicClient.listCancellable.query({
        query: customerId ? { customerId } : {},
      }),
    enabled: isSuperadmin,
  })

  const rows: CancellableRow[] =
    listResponse?.status === 200 ? (listResponse.body as CancellableRow[]) : []

  const issued = useMemo(
    () => rows.filter((r) => r.status === 'issued'),
    [rows],
  )
  const cancelPending = useMemo(
    () => rows.filter((r) => r.status === 'cancel_pending'),
    [rows],
  )

  const cancelMutation = useMutation({
    mutationFn: (vars: { attemptIds: string[]; dryRun: boolean }) =>
      classicClient.cancel.mutate({ body: vars }),
  })

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const toggleGroup = (group: CancellableRow[], checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev)
      group.forEach((r) =>
        checked ? next.add(r.attemptId) : next.delete(r.attemptId),
      )
      return next
    })

  const runCancel = async (dryRun: boolean) => {
    const attemptIds = [...selected]
    if (attemptIds.length === 0) return
    try {
      const response = await cancelMutation.mutateAsync({ attemptIds, dryRun })
      if (response.status === 200) {
        const body = response.body as CancelResult
        setResult(body)
        if (!dryRun) {
          toast({
            title: 'Cancellation done',
            description: `${body.cancelled} cancelled, ${body.failed} failed, ${body.skipped} skipped.`,
          })
          setSelected(new Set())
          await refetch()
        }
      } else {
        toast({
          title: 'Request failed',
          description:
            (response.body as { message?: string })?.message ??
            `Server returned ${response.status}`,
          variant: 'destructive',
        })
      }
    } catch (error) {
      toast({
        title: 'Request failed',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      })
    }
  }

  if (userLoading) return <Loading />
  if (!isSuperadmin) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold">Cancel gift cards</h1>
        <p className="mt-2 text-muted-foreground">
          This page is available to Superadmins only.
        </p>
      </div>
    )
  }

  const renderGroup = (
    title: string,
    description: string,
    group: CancellableRow[],
  ) => {
    const allSelected =
      group.length > 0 && group.every((r) => selected.has(r.attemptId))
    return (
      <section className="rounded-md border border-base-input bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-input">
          <div>
            <h2 className="font-semibold">
              {title}{' '}
              <span className="text-muted-foreground font-normal">
                ({group.length})
              </span>
            </h2>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        {group.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">None.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(c) => toggleGroup(group, c === true)}
                    aria-label={`Select all ${title}`}
                  />
                </TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Incentive</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Card</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.map((r) => (
                <TableRow key={r.attemptId}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(r.attemptId)}
                      onCheckedChange={() => toggle(r.attemptId)}
                      aria-label={`Select ${r.attemptId}`}
                    />
                  </TableCell>
                  <TableCell>
                    {r.campaignName ?? '—'}
                    {r.isTest && (
                      <Badge variant="secondary" className="ml-2">
                        Test
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{r.incentiveName ?? '—'}</TableCell>
                  <TableCell className="tabular-nums">
                    {formatValue(r.value, r.currency)}
                  </TableCell>
                  <TableCell>
                    {r.cardIdLast4 ? `•••• ${r.cardIdLast4}` : '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDate(r.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>
    )
  }

  const selectedCount = selected.size

  return (
    <div className="flex flex-col gap-4 p-4">
      <header>
        <h1 className="text-2xl font-bold">Cancel gift cards</h1>
        <p className="text-sm text-muted-foreground">
          Void Intersolve Classic transactions. <strong>Issued</strong> = live
          cards; <strong>cancel-pending</strong> = failed issues the automatic
          cleanup gave up on. This voids real value — preview first.
        </p>
      </header>

      <div className="flex items-center gap-2">
        <label htmlFor="customerFilter" className="text-sm font-medium">
          Customer
        </label>
        <select
          id="customerFilter"
          value={customerId}
          onChange={(e) => {
            // Selection refers to the currently-listed rows; drop it so a
            // stale id from another customer can't be cancelled after switching.
            setCustomerId(e.target.value)
            setSelected(new Set())
            setResult(null)
          }}
          className="border border-base-input rounded px-3 py-2 text-sm"
        >
          <option value="">All customers</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3 sticky top-0 z-10 rounded-lg border border-base-input bg-white px-4 py-2 shadow-sm">
        <span className="text-sm text-muted-foreground">
          <strong className="text-foreground">{selectedCount}</strong> selected
        </span>

        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="ghost"
            size="sm"
            disabled={listLoading || isRefetching}
            onClick={() => refetch()}
          >
            <ArrowsClockwise weight="regular" />
            Refresh
          </Button>
          {/* Safe rehearsal — outline keeps it visually distinct from the
              destructive cancel so the two are never confused at a glance. */}
          <Button
            variant="outline"
            disabled={selectedCount === 0 || cancelMutation.isPending}
            onClick={() => runCancel(true)}
          >
            <Eye weight="regular" />
            Preview (dry-run)
          </Button>
          <Button
            variant="destructive"
            disabled={selectedCount === 0 || cancelMutation.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            <Prohibit weight="regular" />
            Cancel selected
          </Button>
        </div>
      </div>

      {result && (
        <div className="rounded-md border border-base-input bg-white p-4 text-sm">
          <p className="font-semibold">
            {result.dryRun ? 'Dry-run preview' : 'Cancellation result'} —
            requested {result.requested}, cancelled {result.cancelled}, would
            cancel {result.wouldCancel}, failed {result.failed}, skipped{' '}
            {result.skipped}
          </p>
          <ul className="mt-2 space-y-1">
            {result.results.map((o) => (
              <li key={o.attemptId} className="text-muted-foreground">
                <span className="font-mono">{o.attemptId.slice(0, 8)}</span> —{' '}
                {o.outcome}
                {o.via ? ` (via ${o.via})` : ''}
                {o.reason ? `: ${o.reason}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {listLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No cancellable transactions.
        </p>
      ) : (
        <>
          {renderGroup(
            'Issued (live cards)',
            'Successfully issued cards — cancelled by transaction id.',
            issued,
          )}
          {renderGroup(
            'Cancel pending (needs manual cancel)',
            'Failed issues whose automatic cancel exhausted its retries — cancelled by ref-pos.',
            cancelPending,
          )}
        </>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel {selectedCount} transaction(s)?</DialogTitle>
            <DialogDescription>
              This voids the selected gift-card transactions at Intersolve and
              cannot be undone. Make sure you previewed the dry-run first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Keep them
            </Button>
            <Button
              variant="destructive"
              disabled={cancelMutation.isPending}
              onClick={async () => {
                setConfirmOpen(false)
                await runCancel(false)
              }}
            >
              {cancelMutation.isPending ? 'Cancelling…' : 'Yes, cancel them'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
